import React, { FC } from 'react';
import {
  Text,
  Box,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalSize,
  ButtonAppearance,
  Button,
  ModalClose,
  Separator,
} from '@bechtel/gds';
import { t, Trans } from '@lingui/macro';
import { SaveTimeSheetErrorProps } from './types';
import { SaveTimesheetChargesError, SaveTimesheetError } from '@bechtel/types';
import { useName } from '@bechtel/client-common';
import { find, map } from 'lodash';
import { ApolloError } from '@apollo/client';

const getErrorMessage = (error: string) => {
  switch (error) {
    case SaveTimesheetError.TIMESHEET_NO_LONGER_EDITABLE:
      return <Trans>Timesheet no longer editable.</Trans>;
    case SaveTimesheetError.REASONS_REQUIRED_FOR_TIMETYPE:
      return <Trans>Reason required for time type.</Trans>;
    case SaveTimesheetError.USER_ABSENT_BUT_TIME_PROVIDED:
      return <Trans>User absent but time approved.</Trans>;
    case SaveTimesheetError.INVALID_COST_CODE_PROVIDED:
      return <Trans>Invalid cost code provided.</Trans>;
    case SaveTimesheetError.CERT_CLASS_MISSING_FOR_ONE_OR_MORE_EMPLOYEES:
      return <Trans>Cert class missing for one or more employee.</Trans>;
    case SaveTimesheetError.POSITION_CODE_MISSING_FOR_ONE_OR_MORE_EMPLOYEES:
      return <Trans>Position code missing for one or more employee</Trans>;
    case SaveTimesheetError.MISSING_TIME_CHARGES:
      return <Trans>Missing time charges.</Trans>;
    case SaveTimesheetError.INVALID_CREW_ID:
      return <Trans>Invalid crew id.</Trans>;
    case SaveTimesheetError.INVALID_CREW_MEMBER_ID:
      return <Trans>Invalid crew member id.</Trans>;
    case SaveTimesheetError.SAVE_TIMESHEET_CHARGES_ERRORED:
      return <Trans>Save timesheet charges errored.</Trans>;
    case SaveTimesheetError.SAVE_TIMESHEET_ERRORED:
      return <Trans>Save timesheet errored.</Trans>;
    default:
      return <Trans>An error has occured. Please click close button and try again.</Trans>;
  }
};

export const SaveTimeSheetError: FC<SaveTimeSheetErrorProps> = ({ errors, crewMemberList, close }) => {
  const { getFullName } = useName();
  return (
    <Modal visible size={ModalSize.DEFAULT} style={{ width: '450px', maxHeight: '450px' }}>
      <ModalHeader title={t`Save TimeSheet Error`}>
        <ModalClose onClick={close}></ModalClose>
      </ModalHeader>
      <ModalBody>
        <Box pb={{ sm: '500' }} flexGrow={{ sm: 1 }} flexDirection={{ sm: 'column' }} css={{ minHeight: '150px' }}>
          {errors instanceof ApolloError && (
            <Box p={{ sm: '500' }}>
              <Text>
                <Trans>Network error has occured.Please close button and try again.</Trans>
              </Text>
            </Box>
          )}
          {errors instanceof Array &&
            map(errors, (saveError: SaveTimesheetChargesError) => {
              const crewMember =
                saveError?.crewMemberId &&
                find(crewMemberList, (crewMember) => crewMember.id === saveError?.crewMemberId);
              return (
                <Box key={saveError?.crewMemberId} flexDirection={{ sm: 'column' }}>
                  <Box p={{ sm: '500' }}>
                    {crewMember && (
                      <Text weight="600">{getFullName(crewMember?.firstName, crewMember?.lastName)}-</Text>
                    )}
                    <Box ml={{ sm: '200' }}>
                      <Text>{getErrorMessage(saveError?.error)}</Text>
                    </Box>
                  </Box>
                  {errors.length > 1 && <Separator spacing="000" color="grey.200" />}
                </Box>
              );
            })}
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box justifyContent={{ sm: 'flex-end' }}>
          <Box mr={{ sm: '400' }}>
            <Button
              appearance={ButtonAppearance.SECONDARY}
              onClick={close}
              css={`
                padding-left: ${({ theme }) => theme.spacings['600']};
                padding-right: ${({ theme }) => theme.spacings['600']};
              `}
            >
              <Trans> Close</Trans>
            </Button>
          </Box>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
